/**
 * ReceiptTemplates.jsx — Admin/Manager view to manage saved supplier templates
 * that the Receiving > Create Receipt dialog auto-fills from.
 *
 * Templates are unique per (company_id, supplier_lc). This page lists them all,
 * and lets a manager rename, edit the line items, or delete a template without
 * having to first start a receipt. Editing reuses POST /api/receipt-templates
 * which is an upsert — so it cleanly overwrites the existing row.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  FileText, Plus, X, FloppyDisk, Trash, PencilSimple, Package,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  getReceiptTemplates, saveReceiptTemplate, deleteReceiptTemplate,
  getVariants, getSkus,
} from '../lib/api';
import SkuVariantPicker from '../components/SkuVariantPicker';

const EMPTY_LINE = { variant_id: '', expected_qty: '' };

export default function ReceiptTemplates() {
  const [templates, setTemplates] = useState([]);
  const [variants, setVariants] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState(null); // template object or 'new'
  const [editName, setEditName] = useState('');
  const [editSupplier, setEditSupplier] = useState('');
  const [editLines, setEditLines] = useState([{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tplRes, varsRes, skusRes] = await Promise.all([
        getReceiptTemplates(),
        getVariants(),
        getSkus(),
      ]);
      setTemplates(tplRes.data || []);
      setVariants(varsRes.data || []);
      setSkus(skusRes.data || []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((t) =>
      (t.name || '').toLowerCase().includes(term) ||
      (t.supplier_name || '').toLowerCase().includes(term)
    );
  }, [templates, search]);

  const openNew = () => {
    setEditing('new');
    setEditName('');
    setEditSupplier('');
    setEditLines([{ ...EMPTY_LINE }]);
  };

  const openEdit = (t) => {
    setEditing(t);
    setEditName(t.name || t.supplier_name || '');
    setEditSupplier(t.supplier_name || '');
    setEditLines((t.items || []).map((it) => ({
      variant_id: it.variant_id || '',
      expected_qty: String(it.expected_qty || ''),
    })));
    if (!(t.items || []).length) setEditLines([{ ...EMPTY_LINE }]);
  };

  const setEditLine = (i, key, value) =>
    setEditLines((l) => l.map((ln, idx) => idx === i ? { ...ln, [key]: value } : ln));
  const addEditLine = () => setEditLines((l) => [...l, { ...EMPTY_LINE }]);
  const removeEditLine = (i) =>
    setEditLines((l) => l.length > 1 ? l.filter((_, idx) => idx !== i) : l);

  const submitEdit = async () => {
    const supplier = (editSupplier || '').trim();
    if (!supplier) { toast.error('Supplier name is required'); return; }

    // Build items: dedupe by variant, drop blanks/zeros (mirrors the rule used
    // by Receiving's Create Receipt + Save-as-template path so the data is
    // consistent across both entry points).
    const acc = new Map();
    for (const ln of editLines) {
      if (!ln.variant_id) continue;
      const qty = parseInt(ln.expected_qty, 10);
      if (!Number.isFinite(qty) || qty <= 0) continue;
      acc.set(ln.variant_id, (acc.get(ln.variant_id) || 0) + qty);
    }
    if (acc.size === 0) { toast.error('Add at least one line with qty > 0'); return; }

    const items = Array.from(acc.entries()).map(([variant_id, expected_qty]) => {
      const v = variants.find((x) => x.id === variant_id);
      const sku = v && skus.find((s) => s.id === v.sku_id);
      return {
        variant_id,
        expected_qty,
        sku_id: v?.sku_id || null,
        sku_code: sku?.sku_code || null,
        description: sku?.name || null,
        barcode: v?.barcode || null,
        size: v?.size || null,
        color: v?.color || null,
      };
    });

    setSaving(true);
    try {
      const res = await saveReceiptTemplate({
        supplier_name: supplier,
        name: (editName || supplier).trim(),
        items,
      });
      toast.success(res.data?.created ? 'Template created' : 'Template updated');
      setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}" template? Future receipts won't auto-fill from it.`)) return;
    try {
      await deleteReceiptTemplate(t.id);
      toast.success('Template removed');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Layout>
      <div data-testid="receipt-templates-page" className="p-4 md:p-6 max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-muted-foreground text-xs font-semibold uppercase tracking-widest">
              <FileText size={14} weight="fill" /> Receiving · Supplier templates
            </div>
            <h1 className="text-3xl font-bold tracking-tight mt-1">Supplier Templates</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Save reusable line-sets per supplier. The Create Receipt dialog auto-fills from here when the supplier name matches.
            </p>
          </div>
          <Button
            onClick={openNew}
            data-testid="new-template-btn"
            className="bg-brand hover:bg-brand/90 rounded-none"
          >
            <Plus size={16} weight="bold" className="mr-2" /> New Template
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-3 mb-4">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by template name or supplier…"
            data-testid="template-search"
            className="h-10"
          />
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-muted rounded-md animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="templates-empty">
            <Package size={48} className="mx-auto mb-4 text-muted-foreground/40" />
            <p className="text-muted-foreground">
              {templates.length === 0
                ? 'No supplier templates yet. Save your first from a Create Receipt dialog or click "New Template" above.'
                : 'No templates match the current search.'}
            </p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full table-dense">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Supplier</th>
                  <th className="text-right">Lines</th>
                  <th className="text-right">Total Qty</th>
                  <th>Last Updated</th>
                  <th className="text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const totalQty = (t.items || []).reduce((s, it) => s + (parseInt(it.expected_qty, 10) || 0), 0);
                  return (
                    <tr key={t.id} data-testid={`template-row-${t.supplier_name}`} className="hover:bg-muted">
                      <td className="font-bold">{t.name}</td>
                      <td className="text-muted-foreground">{t.supplier_name}</td>
                      <td className="font-mono text-right">{(t.items || []).length}</td>
                      <td className="font-mono text-right">{totalQty}</td>
                      <td className="text-xs text-muted-foreground">
                        {t.updated_at ? new Date(t.updated_at).toLocaleString() : '—'}
                      </td>
                      <td className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => openEdit(t)}
                            data-testid={`edit-template-${t.supplier_name}`}
                            className="px-2 py-1 text-xs font-bold uppercase tracking-widest border border-brand text-brand hover:bg-brand hover:text-white transition"
                            title="Edit"
                          >
                            <PencilSimple size={12} weight="bold" className="inline mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(t)}
                            data-testid={`delete-template-${t.supplier_name}`}
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                            title="Delete"
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="border border-border rounded-none max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing === 'new' ? 'New Template' : `Edit "${editing?.name || ''}"`}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Templates auto-fill the Create Receipt dialog when the supplier name matches (case-insensitive).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="overline">Supplier Name</Label>
                <Input
                  value={editSupplier}
                  onChange={(e) => setEditSupplier(e.target.value)}
                  placeholder="Acme Supplies"
                  data-testid="template-supplier-input"
                  className="mt-1"
                  disabled={editing !== 'new'}
                />
                {editing !== 'new' && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Supplier name is the key — delete &amp; re-create to change it.
                  </p>
                )}
              </div>
              <div>
                <Label className="overline">Template Name</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder={editSupplier || 'Weekly delivery'}
                  data-testid="template-name-input"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="overline">Line Items</Label>
                  <p className="text-[11px] text-muted-foreground">Variant + expected qty per row.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEditLine}
                  data-testid="template-add-line-btn"
                  className="rounded-none"
                >
                  <Plus size={14} className="mr-1" /> Add Line
                </Button>
              </div>
              <div className="border border-border rounded-md">
                <table className="w-full table-dense">
                  <thead className="sticky top-0 bg-card">
                    <tr>
                      <th>Variant / SKU</th>
                      <th className="text-right w-32">Expected Qty</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {editLines.map((ln, i) => (
                      <tr key={i} data-testid={`template-line-${i}`}>
                        <td>
                          <SkuVariantPicker
                            skus={skus}
                            variants={variants}
                            value={ln.variant_id}
                            onChange={(vid) => setEditLine(i, 'variant_id', vid)}
                            testIdPrefix={`template-picker-${i}`}
                            placeholder="Type SKU code, name, or barcode…"
                          />
                        </td>
                        <td className="text-right">
                          <Input
                            type="number"
                            inputMode="numeric"
                            min={1}
                            value={ln.expected_qty}
                            onChange={(e) => setEditLine(i, 'expected_qty', e.target.value)}
                            data-testid={`template-qty-${i}`}
                            className="text-right font-mono"
                          />
                        </td>
                        <td className="text-right">
                          {editLines.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeEditLine(i)}
                              data-testid={`template-remove-line-${i}`}
                              className="text-muted-foreground hover:text-red-600"
                              aria-label="Remove line"
                            >
                              <X size={14} weight="bold" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Button
              onClick={submitEdit}
              disabled={saving}
              data-testid="template-save-btn"
              className="w-full bg-brand hover:bg-brand/90 rounded-none"
            >
              <FloppyDisk size={14} className="mr-1" />
              {saving ? 'Saving…' : (editing === 'new' ? 'Create Template' : 'Save changes')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
